import { useState, type CSSProperties } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { abrir, fila } from '../components/movimiento';
import { api, type Rol, type UsuarioAdmin } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useSesion } from '../lib/sesion';
import {
  Buscador,
  Cargando,
  Encabezado,
  EstadoChip,
  filtrar,
  Icono,
  Kpi,
  Numero,
  Paginador,
  usePaginado,
  PanelError,
  useConfirmar,
  Vacio,
} from '../components/ui';
import {
  altoMinimo,
  aviso,
  botonPrimario,
  c,
  campo,
  encabezadoTabla,
  etiquetaCampo,
  filaTabla,
  mono,
  nota,
  tarjeta,
} from '../theme';

// La tabla en PC es la de siempre (los 16 px entre columnas son 8 + 8 de cada
// celda); en el celular `.wms-tarjetas` la vuelve una tarjeta por persona.
const cabeza: CSSProperties = { ...encabezadoTabla, display: undefined, gap: undefined, alignItems: undefined, padding: undefined, paddingBlock: 10, textAlign: 'left', whiteSpace: 'nowrap' };
const celda: CSSProperties = { ...filaTabla, display: undefined, gap: undefined, alignItems: undefined, padding: undefined, paddingBlock: 12, verticalAlign: 'middle' };
const lado = 'px-2 first:pl-4 last:pr-4';

const TONO_ROL: Record<Rol, 'ok' | 'warn' | 'neu'> = {
  admin: 'warn',
  operador: 'ok',
  operador_zf: 'neu',
};

export default function Usuarios() {
  const yo = useSesion();
  const { datos, cargando, error, recargar } = useApi(() => api.usuarios(), [], VIVO.normal);

  const [mail, setMail] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState<Rol>('operador_zf');
  const [avisoTexto, setAvisoTexto] = useState<{ texto: string; ok: boolean } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [confirmar, dialogo] = useConfirmar();

  const encontrados = filtrar(datos?.usuarios ?? [], busqueda, (u) => [u.nombre, u.mail, u.rolEtiqueta]);
  const pagina = usePaginado(encontrados);

  async function invitar() {
    setOcupado(true);
    setAvisoTexto(null);
    try {
      const r = await api.crearUsuario({ mail: mail.trim(), nombre: nombre.trim(), rol });
      setAvisoTexto({ texto: `${r.mail} quedó habilitado como ${r.rolEtiqueta}.`, ok: true });
      setMail('');
      setNombre('');
      recargar();
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
    } finally {
      setOcupado(false);
    }
  }

  async function cambiar(u: UsuarioAdmin, nuevo: Rol) {
    if (u.id === null) return;
    const destino = datos?.roles.find((r) => r.id === nuevo);
    const etiqueta = destino?.etiqueta ?? nuevo;
    // El select no se mueve hasta confirmar: su valor sale de u.rol.
    const ok = await confirmar(
      nuevo === 'admin'
        ? { titulo: `¿Hacer administrador a ${u.nombre}?`, detalle: destino?.descripcion, boton: 'Hacerlo administrador' }
        : {
            titulo: `¿Cambiar a ${u.nombre} de ${u.rolEtiqueta} a ${etiqueta}?`,
            detalle: destino?.descripcion,
            boton: `Pasarlo a ${etiqueta}`,
          },
    );
    if (!ok) return;
    setAvisoTexto(null);
    try {
      await api.cambiarRol(u.id, nuevo);
      setAvisoTexto({ texto: `${u.mail} ahora es ${etiqueta}.`, ok: true });
      recargar();
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
    }
  }

  async function quitar(u: UsuarioAdmin) {
    if (u.id === null) return;
    setAvisoTexto(null);
    try {
      if (u.bloqueado) {
        await api.devolverAcceso(u.id);
        setAvisoTexto({ texto: `${u.mail} volvió a tener acceso como ${u.rolEtiqueta}.`, ok: true });
      } else {
        const ok = await confirmar({
          titulo: `¿Quitarle el acceso a ${u.nombre}?`,
          detalle: `${u.mail} no va a poder entrar y su sesión abierta se cierra ahora. Después se le puede devolver.`,
          boton: 'Quitar acceso y cerrar su sesión',
        });
        if (!ok) return;
        await api.quitarUsuario(u.id);
        setAvisoTexto({ texto: `Se le quitó el acceso a ${u.mail}. Su sesión se cerró.`, ok: true });
      }
      recargar();
    } catch (err) {
      setAvisoTexto({ texto: mensajeDeError(err), ok: false });
    }
  }

  if (error) return <PanelError mensaje={error} onReintentar={recargar} />;
  if (cargando && !datos) return <Cargando texto="Buscando usuarios…" />;
  if (!datos) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1100 }}>
      <Encabezado
        bajada={
          datos.dominios.length
            ? `Las cuentas de ${datos.dominios.join(' y ')} entran solas como operadores. Para el resto, hay que darlas de alta acá.`
            : 'Nadie se da de alta solo: todas las cuentas hay que cargarlas acá.'
        }
      />

      <AnimatePresence>
        {avisoTexto && (
          <motion.div key={avisoTexto.texto} {...abrir(10)} role={avisoTexto.ok ? 'status' : 'alert'} style={aviso(avisoTexto.ok ? 'ok' : 'err')}>
            {avisoTexto.texto}
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))', gap: 16 }}>
        <motion.div {...fila(0)} className="[&>*]:h-full">
          <Kpi rotulo="Con acceso" valor={datos.total} pie="personas en total" />
        </motion.div>
        {datos.porRol.map((r, i) => (
          <motion.div key={r.rol} {...fila(i + 1)} className="[&>*]:h-full">
            <Kpi rotulo={r.etiqueta} valor={r.cantidad} pie={r.cantidad === 1 ? 'persona' : 'personas'} />
          </motion.div>
        ))}
      </div>

      <motion.div {...abrir(16)} className="p-4 tablet:p-5" style={{ ...tarjeta, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Dar de alta a alguien</span>
          <div style={{ marginTop: 4, fontSize: 13, color: c.texto2, textWrap: 'pretty' }}>
            Es la única forma de que entre una cuenta de un dominio que no esté habilitado. Después entra con su
            propio Google.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12, alignItems: 'end' }}>
          <label style={etiquetaCampo}>
            Mail
            <input
              type="email"
              value={mail}
              onChange={(e) => setMail(e.target.value)}
              placeholder="nombre@zf.com"
              style={{ ...campo, fontFamily: mono, fontSize: 13 }}
            />
          </label>
          <label style={etiquetaCampo}>
            Nombre <span style={{ color: c.texto3, fontWeight: 400 }}>(opcional)</span>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Se deduce del mail" style={campo} />
          </label>
          <label style={etiquetaCampo}>
            Rol
            <select value={rol} onChange={(e) => setRol(e.target.value as Rol)} style={{ ...campo, padding: '0 12px' }}>
              {datos.roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.etiqueta}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={invitar}
            disabled={ocupado || !mail.trim()}
            style={{ ...botonPrimario, minHeight: altoMinimo(42), opacity: ocupado || !mail.trim() ? 0.5 : 1 }}
          >
            Dar de alta
          </button>
        </div>

        <div style={{ ...nota, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {datos.roles.map((r) => (
            <div key={r.id}>
              <strong style={{ color: c.texto }}>{r.etiqueta}:</strong> {r.descripcion}
            </div>
          ))}
        </div>
      </motion.div>

      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        <div className="flex flex-wrap items-center gap-3" style={{ padding: '14px 16px', borderBottom: `1px solid ${c.borde}` }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: c.texto }}>Con acceso</span>
          <span className="min-w-0 flex-1 basis-[240px] justify-end" style={{ display: 'flex' }}>
            <Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Nombre, mail o rol" ancho={300} />
          </span>
          <span style={{ fontSize: 12.5, color: c.texto3 }}><Numero valor={datos.usuarios.length} /></span>
        </div>

        <div className="wms-scroll-x">
          <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th className={lado} style={{ ...cabeza, minWidth: 200 }}>Persona</th>
                <th className={lado} style={{ ...cabeza, width: 190 }}>Rol</th>
                <th className={lado} style={{ ...cabeza, width: 150 }}>Último ingreso</th>
                <th className={lado} style={{ ...cabeza, width: 110, textAlign: 'right' }}>Acceso</th>
              </tr>
            </thead>

            {/* Al buscar las que quedan se deslizan a su lugar; al cambiar de página entran otras. */}
            <tbody>
            {pagina.visibles.map((u, i) => (
              <motion.tr
                key={u.mail}
                layout="position"
                {...fila(i)}
                // Quitar o devolver el acceso apaga o enciende la fila, no la cambia de golpe.
                animate={{ opacity: u.bloqueado ? 0.6 : 1, y: 0 }}
              >
                <td className={`${lado} wms-principal`} style={{ ...celda, minWidth: 200 }}>
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, alignItems: 'flex-start' }}>
                  <span style={{ fontWeight: 500 }}>
                    {u.nombre}
                    {u.mail === yo.mail && <span style={{ color: c.texto3, fontWeight: 400 }}> · vos</span>}
                  </span>
                  <span style={{ fontFamily: mono, fontSize: 12, color: c.texto3 }}>{u.mail}</span>
                  {u.bloqueado && (
                    <span style={{ fontSize: 12, color: c.rojo, fontWeight: 500 }}>Sin acceso</span>
                  )}
                </span>
                </td>

                <td className={lado} data-rotulo="Rol" style={celda}>
                  {u.fijo || u.id === null ? (
                    <span title="Administrador fijo del sistema">
                      <EstadoChip estado={u.rolEtiqueta} tono={TONO_ROL[u.rol]} />
                    </span>
                  ) : (
                    <select
                      value={u.rol}
                      onChange={(e) => cambiar(u, e.target.value as Rol)}
                      aria-label={`Rol de ${u.nombre}`}
                      style={{
                        height: 34,
                        padding: '0 10px',
                        border: `1px solid ${c.bordeFuerte}`,
                        borderRadius: 999,
                        background: c.panel,
                        fontSize: 13,
                        color: c.texto,
                      }}
                    >
                      {datos.roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.etiqueta}
                        </option>
                      ))}
                    </select>
                  )}
                </td>

                <td className={lado} data-rotulo="Último ingreso" style={{ ...celda, fontSize: 12.5, color: u.nuncaEntro ? c.texto4 : c.texto2 }}>
                  {u.ultimoIngreso ?? (u.invitadoPor ? 'nunca entró' : 'pendiente de ingreso')}
                </td>

                <td className={`${lado} wms-acciones`} style={{ ...celda, textAlign: 'right' }}>
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {u.fijo || u.id === null || u.mail === yo.mail ? (
                    <span style={{ fontSize: 12, color: c.texto4 }}>{u.fijo ? 'fijo' : '—'}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => quitar(u)}
                      aria-label={u.bloqueado ? `Devolverle el acceso a ${u.mail}` : `Quitar el acceso a ${u.mail}`}
                      title={u.bloqueado ? 'Devolver el acceso' : 'Quitar el acceso'}
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 999,
                        border: `1px solid ${c.borde}`,
                        background: c.panel,
                        color: u.bloqueado ? c.texto2 : c.rojo,
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      <Icono nombre={u.bloqueado ? 'person_add' : 'person_remove'} size={18} />
                    </button>
                  )}
                  </span>
                </td>
              </motion.tr>
            ))}
            </tbody>
          </table>
        </div>

        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="personas"
          onIr={pagina.setPagina}
        />

        {datos.usuarios.length === 0 && (
          <Vacio icono="group" titulo="Todavía no entró nadie" detalle="Los usuarios aparecen acá al dar de alta o al entrar por primera vez." />
        )}
      </div>

      <div style={nota}>
        Los marcados como <strong>fijos</strong> son los administradores fijos del sistema: no se les puede cambiar el
        rol ni quitar el acceso desde acá. Es a propósito, para que siempre quede alguien que pueda administrar.
      </div>
      {dialogo}
    </div>
  );
}
